import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, withKeys as _withKeys, vShow as _vShow, withDirectives as _withDirectives, Transition as _Transition, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = ["aria-expanded"];
import { reactive, ref, inject, computed, watch } from 'vue';
import { useFocusWithin } from '@vueuse/core';
import { throttle } from '@/helpers';
import { UserFilled } from '@element-plus/icons-vue';
export default {
  __name: 'UserMenu',
  setup(__props) {
    const user = inject('user');
    const focusTrap = inject("focusTrap", {
      focusNextElement: () => {},
      focusPrevElement: () => {}
    });
    const dropdown = ref(null);
    const dropdownHeader = ref(null);
    const dropdownContent = ref(null);
    const {
      focused
    } = useFocusWithin(dropdown);
    const state = reactive({
      visible: false
    });

    /**
     * Returns a boolean indicating whether a red attention badge should be shown by the user's name/icon.
     * @returns {boolean}
     */
    const showBadge = computed(() => {
      return user.hasAvailableUpgrades;
    });

    // Methods

    const handleOpen = () => {
      state.visible = true;
    };
    const handleClose = () => {
      state.visible = false;
    };
    const toggleDropdown = () => {
      state.visible = !state.visible;
    };
    const getDropdownItems = () => {
      return dropdownContent.value?.getFocusableElements() ?? [];
    };
    const handleMenuClose = () => {
      handleClose();
      dropdownHeader.value?.$el.focus();
    };
    const handleDropdownKeydownLeft = event => {
      if (!state.visible) {
        return;
      }
      event.stopPropagation();
      event.preventDefault();
      handleMenuClose();
      focusTrap.focusPrevElement();
    };
    const handleDropdownKeydownRight = event => {
      if (!state.visible) {
        return;
      }
      event.stopPropagation();
      event.preventDefault();
      handleMenuClose();
      focusTrap.focusNextElement();
    };
    const handleSubmenuOpen = throttle((start = "first") => {
      // Defer update to the next tick
      setTimeout(() => {
        const dropdownItems = getDropdownItems();
        if (dropdownItems.length > 0) {
          if (start === "first") {
            dropdownItems[0]?.focus();
          } else if (start === "last") {
            dropdownItems[dropdownItems.length - 1]?.focus();
          }
        }
      }, 0);
    }, 200); // Throttle to avoid multiple calls in quick succession while the dropdown is opening

    const handleHeaderKeydown = event => {
      switch (event.key) {
        case ' ':
        case 'Enter':
        case 'ArrowDown':
          event.preventDefault();
          handleOpen();
          handleSubmenuOpen("first");
          break;
        case 'ArrowUp':
          event.preventDefault();
          handleOpen();
          handleSubmenuOpen("last");
          break;
      }
    };
    const handleLogout = () => {
      user.logout();
      handleMenuClose();
    };
    watch(focused, (newVal, oldVal) => {
      if (!newVal && oldVal) {
        handleClose();
      }
    });
    return (_ctx, _cache) => {
      const _component_el_button = _resolveComponent("el-button");
      const _component_el_badge = _resolveComponent("el-badge");
      const _component_router_link = _resolveComponent("router-link");
      const _component_FocusTrap = _resolveComponent("FocusTrap");
      return _openBlock(), _createElementBlock("div", {
        class: "dropdown-container",
        ref_key: "dropdown",
        ref: dropdown,
        "aria-expanded": state.visible,
        onMouseleave: _cache[3] || (_cache[3] = () => handleClose()),
        onKeydown: [_withKeys(handleDropdownKeydownLeft, ["left"]), _withKeys(handleDropdownKeydownRight, ["right"])]
      }, [_createElementVNode("div", {
        onClick: _cache[0] || (_cache[0] = () => toggleDropdown()),
        onKeydown: handleHeaderKeydown
      }, [showBadge.value ? (_openBlock(), _createBlock(_component_el_badge, {
        key: 0,
        type: "primary",
        "is-dot": "",
        offset: [-5, 5]
      }, {
        default: _withCtx(() => [_createVNode(_component_el_button, {
          ref_key: "dropdownHeader",
          ref: dropdownHeader,
          class: "gray-base",
          size: "large",
          type: "primary",
          plain: "",
          icon: _unref(UserFilled),
          circle: "",
          disabled: _unref(user).state.logoutLoading,
          loading: _unref(user).state.logoutLoading,
          "data-testid": "userDropdownButton"
        }, null, 8, ["icon", "disabled", "loading"])]),
        _: 1
      })) : (_openBlock(), _createBlock(_component_el_button, {
        key: 1,
        ref_key: "dropdownHeader",
        ref: dropdownHeader,
        class: "gray-base",
        size: "large",
        type: "primary",
        plain: "",
        icon: _unref(UserFilled),
        circle: "",
        disabled: _unref(user).state.logoutLoading,
        loading: _unref(user).state.logoutLoading,
        "data-testid": "userDropdownButton"
      }, null, 8, ["icon", "disabled", "loading"]))], 32), _createVNode(_component_FocusTrap, {
        ref_key: "dropdownContent",
        ref: dropdownContent,
        escCallback: handleMenuClose
      }, {
        default: _withCtx(() => [_createVNode(_Transition, {
          name: "fast-fade"
        }, {
          default: _withCtx(() => [_withDirectives(_createElementVNode("div", {
            class: "dropdown",
            onClick: handleMenuClose
          }, [_createVNode(_component_router_link, {
            to: {
              name: 'Overview'
            },
            class: "dropdown-option"
          }, {
            default: _withCtx(() => _cache[4] || (_cache[4] = [_createElementVNode("div", null, " My Account ", -1)])),
            _: 1
          }), _createVNode(_component_router_link, {
            to: {
              name: 'Profile'
            },
            class: "dropdown-option"
          }, {
            default: _withCtx(() => _cache[5] || (_cache[5] = [_createElementVNode("div", null, " Account Settings ", -1)])),
            _: 1
          }), _createVNode(_component_router_link, {
            to: {
              name: 'Orders'
            },
            class: "dropdown-option"
          }, {
            default: _withCtx(() => _cache[6] || (_cache[6] = [_createElementVNode("div", null, " Order History ", -1)])),
            _: 1
          }), _unref(user).hasAvailableUpgrades ? (_openBlock(), _createBlock(_component_router_link, {
            key: 0,
            to: {
              name: 'UserUpgrades'
            },
            class: "dropdown-option"
          }, {
            default: _withCtx(() => [_createVNode(_component_el_badge, {
              type: "primary",
              value: _unref(user).summary.available_upgrades,
              class: "upgrades-badge"
            }, {
              default: _withCtx(() => _cache[7] || (_cache[7] = [_createElementVNode("div", null, " Available Offers ", -1)])),
              _: 1
            }, 8, ["value"])]),
            _: 1
          })) : _createCommentVNode("", true), _createElementVNode("div", {
            class: "dropdown-option",
            onClick: _cache[1] || (_cache[1] = () => handleLogout()),
            onKeydown: _cache[2] || (_cache[2] = _withKeys(() => handleLogout(), ["enter"])),
            tabindex: "0",
            role: "button"
          }, " Logout ", 32)], 512), [[_vShow, state.visible]])]),
          _: 1
        })]),
        _: 1
      }, 512)], 40, _hoisted_1);
    };
  }
};